import React, { useState, useEffect } from 'react';
import { settingsStorage, isValidAuthorizationKey } from '../settings';
import { Settings as SettingsIcon, Eye, EyeOff, Save, X, Trash2, Check, AlertCircle } from 'lucide-react';

interface SettingsPageState {
    authorizationKey: string;
    isEditing: boolean;
    saveStatus: 'idle' | 'saving' | 'saved' | 'error';
    validationError?: string;
    showKey: boolean;
    hasExistingKey: boolean;
}

const Settings = () => {
    const [state, setState] = useState<SettingsPageState>({
        authorizationKey: '',
        isEditing: false,
        saveStatus: 'idle',
        showKey: false,
        hasExistingKey: false,
    });

    // Load existing settings on component mount
    useEffect(() => {
        const existingKey = settingsStorage.getAuthorizationKey();
        if (existingKey) {
            setState(prev => ({
                ...prev,
                authorizationKey: existingKey,
                hasExistingKey: true,
            }));
        }
    }, []);

    // Clear save status after a delay
    useEffect(() => {
        if (state.saveStatus === 'saved') {
            const timer = setTimeout(() => {
                setState(prev => ({ ...prev, saveStatus: 'idle' }));
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [state.saveStatus]);

    const handleKeyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setState(prev => ({
            ...prev,
            authorizationKey: value,
            validationError: undefined,
            saveStatus: 'idle',
        }));
    };

    const validateKey = (key: string): string | undefined => {
        if (!key.trim()) {
            return 'Authorization cookie is required';
        }

        if (!isValidAuthorizationKey(key)) {
            if (key.length < 1) {
                return 'Cookie must not be empty';
            }
        }

        return undefined;
    };

    const handleSave = async () => {
        const trimmedKey = state.authorizationKey.trim();
        const validationError = validateKey(trimmedKey);

        if (validationError) {
            setState(prev => ({ ...prev, validationError }));
            return;
        }

        setState(prev => ({ ...prev, saveStatus: 'saving', validationError: undefined }));

        try {
            settingsStorage.setAuthorizationKey(trimmedKey);
            setState(prev => ({
                ...prev,
                saveStatus: 'saved',
                isEditing: false,
                hasExistingKey: true,
            }));
        } catch (error) {
            setState(prev => ({
                ...prev,
                saveStatus: 'error',
                validationError: error instanceof Error ? error.message : 'Failed to save key',
            }));
        }
    };

    const handleCancel = () => {
        const existingKey = settingsStorage.getAuthorizationKey();
        setState(prev => ({
            ...prev,
            authorizationKey: existingKey || '',
            isEditing: false,
            validationError: undefined,
            saveStatus: 'idle',
        }));
    };

    const handleEdit = () => {
        setState(prev => ({ ...prev, isEditing: true }));
    };

    const handleRemove = () => {
        if (window.confirm('Are you sure you want to remove your authorization cookie? You will need to enter it again to generate videos.')) {
            try {
                settingsStorage.removeAuthorizationKey();
                setState(prev => ({
                    ...prev,
                    authorizationKey: '',
                    isEditing: false,
                    hasExistingKey: false,
                    saveStatus: 'idle',
                    validationError: undefined,
                }));
            } catch {
                setState(prev => ({
                    ...prev,
                    validationError: 'Failed to remove cookie',
                }));
            }
        }
    };

    const toggleShowKey = () => {
        setState(prev => ({ ...prev, showKey: !prev.showKey }));
    };

    const maskKey = (key: string): string => {
        if (key.length <= 20) return '•'.repeat(key.length);
        // For cookies, show first few chars and last few chars with masking in between
        return key.slice(0, 10) + '•'.repeat(Math.min(key.length - 20, 20)) + key.slice(-10);
    };

    const getDisplayValue = (): string => {
        if (!state.authorizationKey) return '';
        if (state.isEditing || state.showKey) return state.authorizationKey;
        return maskKey(state.authorizationKey);
    };

    const storageType = settingsStorage.isAvailable() ? 'localStorage' : 'session storage';
    const isPersistent = settingsStorage.isAvailable();

    return (
        <div className="page-container">
            <header className="page-header">
                <h2>Settings</h2>
                <p>Configure your authorization cookie for video generation.</p>
            </header>

            <div className="content-card">
                <div className="form-group">
                    <label htmlFor="authorizationKey">
                        Authorization Cookie
                        {!isPersistent && (
                            <span style={{ color: 'var(--warning)', fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                                (Session only - will not persist)
                            </span>
                        )}
                    </label>

                    <div style={{ position: 'relative' }}>
                        <textarea
                            id="authorizationKey"
                            value={getDisplayValue()}
                            onChange={handleKeyChange}
                            placeholder="Paste your cookie or API key here..."
                            disabled={!state.isEditing && state.hasExistingKey}
                            rows={3}
                            style={{
                                paddingRight: '3rem',
                                borderColor: state.validationError ? 'var(--danger)' : undefined,
                                resize: 'vertical',
                                minHeight: '80px',
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                            }}
                        />

                        {state.hasExistingKey && !state.isEditing && (
                            <button
                                type="button"
                                onClick={toggleShowKey}
                                className="btn-icon"
                                style={{
                                    position: 'absolute',
                                    right: '0.5rem',
                                    top: '0.5rem',
                                }}
                                title={state.showKey ? 'Hide cookie' : 'Show cookie'}
                            >
                                {state.showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                        )}
                    </div>

                    {state.validationError && (
                        <div style={{
                            color: 'var(--danger)',
                            fontSize: '0.875rem',
                            marginTop: '0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                        }}>
                            <AlertCircle size={16} />
                            {state.validationError}
                        </div>
                    )}

                    {state.saveStatus === 'saved' && (
                        <div style={{
                            color: 'var(--success)',
                            fontSize: '0.875rem',
                            marginTop: '0.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                        }}>
                            <Check size={16} />
                            Authorization cookie saved successfully!
                        </div>
                    )}
                </div>

                <div style={{
                    display: 'flex',
                    gap: '1rem',
                    marginTop: '1.5rem',
                    flexWrap: 'wrap',
                }}>
                    {state.isEditing ? (
                        <>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={state.saveStatus === 'saving'}
                                className="btn-primary"
                            >
                                {state.saveStatus === 'saving' ? (
                                    <>
                                        <div className="animate-spin" style={{ width: '16px', height: '16px', border: '2px solid transparent', borderTop: '2px solid currentColor', borderRadius: '50%' }} />
                                        <span>Saving...</span>
                                    </>
                                ) : (
                                    <>
                                        <Save size={16} />
                                        <span>Save Cookie</span>
                                    </>
                                )}
                            </button>

                            <button
                                type="button"
                                onClick={handleCancel}
                                className="btn-secondary"
                                disabled={state.saveStatus === 'saving'}
                            >
                                <X size={16} />
                                <span>Cancel</span>
                            </button>
                        </>
                    ) : (
                        <>
                            {state.hasExistingKey ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleEdit}
                                        className="btn-primary"
                                    >
                                        <SettingsIcon size={16} />
                                        <span>Edit Cookie</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleRemove}
                                        className="btn-icon danger"
                                        style={{
                                            padding: '0.75rem 1rem',
                                            border: '1px solid var(--danger)',
                                            borderRadius: '0.5rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                        }}
                                        title="Remove authorization cookie"
                                    >
                                        <Trash2 size={16} />
                                        <span>Remove Cookie</span>
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setState(prev => ({ ...prev, isEditing: true }))}
                                    className="btn-primary"
                                >
                                    <SettingsIcon size={16} />
                                    <span>Add Authorization Cookie</span>
                                </button>
                            )}
                        </>
                    )}
                </div>

                <div style={{
                    marginTop: '2rem',
                    padding: '1rem',
                    background: 'var(--input-bg)',
                    borderRadius: '0.5rem',
                    border: '1px solid var(--border)',
                }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                        Storage Information
                    </h4>
                    <p style={{ margin: '0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        Your authorization cookie is stored in {storageType}
                        {isPersistent
                            ? ' and will persist across browser sessions.'
                            : ' and will be lost when you close the browser.'
                        }
                    </p>
                </div>

                <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    background: 'var(--input-bg)',
                    borderRadius: '0.5rem',
                    border: '1px solid var(--border)',
                }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                        Cookie Requirements
                    </h4>
                    <ul style={{ margin: '0', paddingLeft: '1rem', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        <li>Paste your full cookie string or API key here</li>
                        <li>This will be sent as a Bearer token in the Authorization header</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default Settings;
